import { useState } from 'react';
import { ApiError, call } from '../api';
import { Badge, BADGES, badgeId, badgePrefsOf, badgesFromRelease, type BadgePlace, type BadgePrefs, type BadgeStyle } from '../badges';
import { savePrefs, usePrefs } from '../prefs';

const STYLES: [BadgeStyle, string, string][] = [
  ['logo', 'Logo', 'Eigen logo-stijl, zoals in Kodi-skins'],
  ['color', 'Kleur', 'Gekleurde labels per soort'],
  ['text', 'Tekst', 'Rustige omlijnde tekst'],
  ['pack', 'Eigen pakket', 'Plaatjes uit een geïmporteerd pakket'],
];
const PLACES: [BadgePlace, string][] = [['posters', 'Posters'], ['title', 'Titelpagina'], ['sources', 'Bronnenlijst'], ['player', 'Speler']];
const SAMPLE = 'Movie.2024.2160p.UHD.BluRay.REMUX.DV.HDR10.HEVC.TrueHD.Atmos.7.1-GROUP';

/**
 * Badge Studio (zoals Xperience): welke badges, de volgorde, de stijl, waar ze staan, een voorbeeld op een
 * release-naam en een eigen pakket importeren. Geldt per profiel, op het web en in de apps.
 */
export function BadgeStudio() {
  const prefs = usePrefs();
  const bp = badgePrefsOf(prefs);
  const [release, setRelease] = useState(SAMPLE);
  const [packUrl, setPackUrl] = useState('');
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const save = (patch: Partial<BadgePrefs>) => void savePrefs({ badges: { ...bp, ...patch, pack: patch.pack !== undefined ? patch.pack : bp.pack ?? null } });
  const move = (id: string, d: -1 | 1) => {
    const order = [...bp.order];
    const i = order.indexOf(id);
    const j = i + d;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    save({ order });
  };
  const toggle = (id: string) => save({ hidden: bp.hidden.includes(id) ? bp.hidden.filter((x) => x !== id) : [...bp.hidden, id] });

  const importPack = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await call<{ pack: { name: string; images: Record<string, string> } }>('/api/badges/import', { method: 'POST', body: JSON.stringify({ url: packUrl.trim() }) });
      save({ pack: r.pack, style: 'pack' });
      setMsg({ text: `"${r.pack.name}" geïmporteerd: ${Object.keys(r.pack.images).length} badges.` });
      setPackUrl('');
    } catch (err) {
      setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Importeren mislukt.', error: true });
    } finally {
      setBusy(false);
    }
  };

  // Voorbeeld: welke badges krijgt deze release, in jouw volgorde en stijl
  const found = new Set(badgesFromRelease(release).map((l) => badgeId(l)).filter(Boolean) as string[]);
  const preview = bp.order.filter((id) => found.has(id) && !bp.hidden.includes(id));

  return (
    <section className="settings-block badge-studio" aria-label="Badge Studio">
      <h2>Badge Studio</h2>
      <p className="subtle hint">Kies welke badges je ziet (4K, Dolby Vision, Atmos…), de volgorde, de stijl en waar. Geldt voor dit profiel, op het web en in de apps.</p>

      <div className="bs-preview">
        <label className="ua2-select">Voorbeeld op een release-naam
          <input className="text-input" value={release} onChange={(e) => setRelease(e.target.value)} spellCheck={false} />
        </label>
        <div className="bs-preview-row" aria-live="polite">
          {preview.length ? preview.map((id) => <Badge key={id} id={id} label={id} style={bp.style} pack={bp.pack} size="lg" />) : <span className="subtle">Geen badges voor deze naam.</span>}
        </div>
      </div>

      <h3 className="bk-sub">Stijl</h3>
      <div className="qs-cards" role="radiogroup" aria-label="Stijl">
        {STYLES.map(([v, name, hint]) => (
          <button key={v} type="button" role="radio" aria-checked={bp.style === v} disabled={v === 'pack' && !bp.pack} className={`qs-card${bp.style === v ? ' is-on' : ''}`} onClick={() => save({ style: v })}>
            <span className="bs-style-demo">
              {['4k', 'dv', 'atmos'].map((id) => <Badge key={id} id={id} label={id} style={v} pack={bp.pack} size="sm" />)}
            </span>
            <strong>{name}</strong>
            <span>{v === 'pack' && !bp.pack ? 'Importeer eerst een pakket (onderaan)' : hint}</span>
          </button>
        ))}
      </div>

      <h3 className="bk-sub">Waar</h3>
      <div className="qs-chips">
        {PLACES.map(([p, name]) => (
          <button key={p} className="chip" aria-pressed={bp.places[p]} onClick={() => save({ places: { ...bp.places, [p]: !bp.places[p] } })}>{name}</button>
        ))}
      </div>

      <h3 className="bk-sub">Welke en in welke volgorde</h3>
      <ol className="bs-list">
        {bp.order.map((id, i) => {
          const def = BADGES.find((b) => b.id === id)!;
          const on = !bp.hidden.includes(id);
          return (
            <li key={id} className={on ? '' : 'is-off'}>
              <input type="checkbox" className="sw" role="switch" checked={on} onChange={() => toggle(id)} aria-label={`${def.label} tonen`} />
              <span className="bs-badge"><Badge id={id} label={def.label} style={bp.style} pack={bp.pack} size="sm" /></span>
              <span className="bs-name"><strong>{def.label}</strong><span className="subtle">{def.hint}</span></span>
              <span className="bs-move">
                <button className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(id, -1)} aria-label={`${def.label} omhoog`}>▲</button>
                <button className="btn btn-ghost btn-sm" disabled={i === bp.order.length - 1} onClick={() => move(id, 1)} aria-label={`${def.label} omlaag`}>▼</button>
              </span>
            </li>
          );
        })}
      </ol>

      <h3 className="bk-sub">Eigen pakket importeren</h3>
      <p className="subtle hint">Plak een link naar een badge-pakket (JSON met plaatjes, zoals de pakketten voor Nuvio of DexHub). Badges die er niet in zitten, krijgen de logo-stijl.</p>
      <form className="bk-form" onSubmit={(e) => { e.preventDefault(); void importPack(); }}>
        <input className="text-input" type="url" placeholder="https://…/badges.json" value={packUrl} onChange={(e) => setPackUrl(e.target.value)} />
        <button className="btn btn-sm" type="submit" disabled={busy || !packUrl.trim()}>{busy ? 'Bezig…' : 'Importeren'}</button>
        {bp.pack && <button className="btn btn-ghost btn-sm" type="button" onClick={() => save({ pack: null, style: bp.style === 'pack' ? 'logo' : bp.style })}>"{bp.pack.name}" verwijderen</button>}
      </form>
      {msg && <p className={msg.error ? 'error' : 'subtle'} role="status">{msg.text}</p>}

      <button className="btn btn-ghost btn-sm" onClick={() => void savePrefs({ badges: null })} disabled={!prefs.badges}>Terug naar standaard</button>
    </section>
  );
}
